import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { WifiHigh, Plus, Trash, MagnifyingGlass, Paperclip, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtInr = (n) => "₹" + (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const PAYMENT_MODES = ["UPI", "Net Banking", "Debit/Credit Card", "Cash", "Other"];
const EMPTY = {
  hub: "", provider: "", account_id: "", amount: "", recharge_date: new Date().toISOString().slice(0, 10),
  validity_date: "", payment_mode: "UPI", utr: "", remarks: "", file: null, file_name: "",
};

function EntryForm({ hubs, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (user?.hub) setForm((f) => ({ ...f, hub: f.hub || user.hub }));
  }, [user?.hub]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setForm({ ...form, file, file_name: file.name });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setPct(0);
    try {
      let screenshot_file_id = null;
      if (form.file) {
        setPct(1);
        const up = await uploadFile(form.file, setPct);
        screenshot_file_id = up.file_id;
      }
      await api.post("/broadband", {
        hub: form.hub, provider: form.provider, account_id: form.account_id,
        amount: parseFloat(form.amount), recharge_date: form.recharge_date,
        validity_date: form.validity_date, payment_mode: form.payment_mode,
        utr: form.utr, remarks: form.remarks,
        screenshot_file_id, screenshot_name: form.file_name || null,
      });
      toast.success("Broadband recharge saved");
      setForm({ ...EMPTY, hub: form.hub });
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
      setPct(0);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="broadband-form">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-3">New broadband recharge entry</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label>Hub name</Label>
          <Select value={form.hub} onValueChange={(v) => setForm({ ...form, hub: v })}>
            <SelectTrigger data-testid="bb-hub" className="mt-1"><SelectValue placeholder="Choose hub" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {hubs.map((h) => (<SelectItem key={h.id} value={h.name}>{h.name}</SelectItem>))}
              {form.hub && !hubs.some((h) => h.name === form.hub) && (
                <SelectItem value={form.hub}>{form.hub}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="bb-provider">Broadband provider</Label>
          <Input id="bb-provider" data-testid="bb-provider" required value={form.provider} onChange={set("provider")} placeholder="e.g. JioFiber" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="bb-account">Account / Customer ID</Label>
          <Input id="bb-account" data-testid="bb-account" required value={form.account_id} onChange={set("account_id")} className="mt-1 font-mono" />
        </div>
        <div>
          <Label htmlFor="bb-amount">Recharge amount (₹)</Label>
          <Input id="bb-amount" data-testid="bb-amount" required type="number" step="0.01" min="0.01" value={form.amount} onChange={set("amount")} className="mt-1 font-mono" />
        </div>
        <div>
          <Label htmlFor="bb-date">Recharge date</Label>
          <Input id="bb-date" data-testid="bb-date" required type="date" value={form.recharge_date} onChange={set("recharge_date")} className="mt-1 font-mono" />
        </div>
        <div>
          <Label htmlFor="bb-validity">Validity / Due date</Label>
          <Input id="bb-validity" data-testid="bb-validity" type="date" value={form.validity_date} onChange={set("validity_date")} className="mt-1 font-mono" />
        </div>
        <div>
          <Label>Payment mode</Label>
          <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
            <SelectTrigger data-testid="bb-mode" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_MODES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="bb-utr">Transaction / UTR number</Label>
          <Input id="bb-utr" data-testid="bb-utr" value={form.utr} onChange={set("utr")} className="mt-1 font-mono" />
        </div>
        <div className="col-span-2">
          <Label>Bill / payment screenshot</Label>
          <label className="mt-1 flex items-center gap-2 border border-dashed border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-600 cursor-pointer hover:bg-zinc-50">
            <Paperclip size={16} />
            <span className="truncate">{form.file_name || "Attach image / PDF (max 5MB)"}</span>
            <input type="file" data-testid="bb-file" accept="image/*,.pdf" className="hidden" onChange={onFile} />
          </label>
        </div>
        <div className="col-span-2">
          <Label htmlFor="bb-remarks">Remarks</Label>
          <Textarea id="bb-remarks" data-testid="bb-remarks" rows={1} value={form.remarks} onChange={set("remarks")} className="mt-1" />
        </div>
      </div>
      <Button type="submit" disabled={saving || !form.hub} data-testid="bb-submit"
        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
        <Plus size={16} className="mr-1.5" />
        {saving ? (pct > 0 && pct < 100 ? `Uploading ${pct}%…` : "Saving…") : "Save entry"}
      </Button>
    </form>
  );
}

export default function Broadband() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const isHubUser = user?.role === "hub_user";

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (month) params.month = month;
      const { data } = await api.get("/broadband", { params });
      setItems(data);
    } catch {
      toast.error("Could not load broadband entries");
    } finally {
      setLoading(false);
    }
  }, [q, month]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { api.get("/hubs").then(({ data }) => setHubs(data)); }, []);

  const viewScreenshot = async (rec) => {
    try {
      const { data } = await api.get(`/files/${rec.screenshot_file_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch {
      toast.error("Could not open screenshot");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this broadband entry?")) return;
    try {
      await api.delete(`/broadband/${id}`);
      toast.success("Entry deleted");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Utilities</div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-900 flex items-center gap-2">
          <WifiHigh size={24} weight="duotone" className="text-blue-600" /> Broadband recharge
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Manual entry log of broadband recharges across hubs.</p>
      </div>

      <div className="mt-5">
        <EntryForm hubs={hubs} onSaved={fetchList} />
      </div>

      <div className="mt-5 bg-white border border-zinc-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-zinc-200">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="bb-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search hub, provider, account ID, UTR…" className="pl-9" />
          </div>
          <Input type="month" data-testid="bb-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-200">
                <th className="px-4 py-2.5 font-medium">Recharge date</th>
                <th className="px-4 py-2.5 font-medium">Hub</th>
                <th className="px-4 py-2.5 font-medium">Provider</th>
                <th className="px-4 py-2.5 font-medium">Account ID</th>
                <th className="px-4 py-2.5 font-medium">Validity</th>
                <th className="px-4 py-2.5 font-medium">Mode</th>
                <th className="px-4 py-2.5 font-medium">UTR</th>
                <th className="px-4 py-2.5 font-medium">Submitted by</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-500">No broadband entries yet.</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} data-testid={`bb-row-${r.id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.recharge_date}</td>
                  <td className="px-4 py-3 text-zinc-700">{r.hub || "—"}</td>
                  <td className="px-4 py-3 text-zinc-900">{r.provider}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.account_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.validity_date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-700">{r.payment_mode}</td>
                  <td className="px-4 py-3 font-mono text-xs text-green-700">{r.utr || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-zinc-800">{r.submitted_by?.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-zinc-900">{fmtInr(r.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {r.screenshot_file_id && (
                        <button type="button" onClick={() => viewScreenshot(r)}
                          data-testid={`bb-view-${r.id}`} title="View screenshot"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-zinc-200 text-zinc-700 text-xs hover:bg-zinc-100">
                          <Eye size={14} /> Bill
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <Button variant="ghost" size="sm" data-testid={`bb-delete-${r.id}`}
                          onClick={() => remove(r.id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="p-4 text-xs text-zinc-500 font-mono">Loading…</div>}
      </div>
    </DashboardLayout>
  );
}
